import { ConnectorChannelIcon } from "#components/ui/ConnectorChannelIcon.tsx";
import type { ToolApprovalCardProps, ToolApprovalRenderer } from "#components/ui/tool-approval/renderers/ToolApprovalRenderer.types.ts";
import { DraftSurface, EditErrors, QuotedMessage, SentText, textEditor, useDraftMode } from "#features/conversations/components/prepared-action/cardControls.tsx";
import { DraftTextArea } from "#features/conversations/components/prepared-action/DraftTextArea.tsx";
import { isObject, readString, toHttpsUrl, type Fields } from "#features/conversations/lib/untypedFields.ts";
import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import { Link, Text } from "@hopper-ui/components";
import { IconOpenLink } from "@intentui/icons";
import type { ReactNode } from "react";
import "./MessageCard.css";

const SLACK_MENTION = /<@([A-Z0-9]+)(?:\|[^>]*)?>/g;

// A thread link opens only on Slack, whatever core sends.
function toSlackUrl(value: string | null): string | null {
    const url = toHttpsUrl(value);
    if (url === null) {
        return null;
    }
    const { hostname } = new URL(url);

    return hostname === "slack.com" || hostname.endsWith(".slack.com") ? url : null;
}

// A mention reads as the name core resolved for it; one core could not name stays as Slack wrote it.
function withMentionNames(text: string, names: Fields): ReactNode[] {
    const parts: ReactNode[] = [];
    let last = 0;
    for (const match of text.matchAll(SLACK_MENTION)) {
        const name = readString(names, match[1] ?? "");
        parts.push(text.slice(last, match.index), name !== null ? <Text key={match.index} size="sm" fontWeight="body-sm-medium" color="neutral">@{name}</Text> : match[0]);
        last = match.index + match[0].length;
    }

    return [...parts, text.slice(last)];
}

// The text shows as written, the mrkdwn source the field edits, never as markup or links.
function SlackMessageCard(props: ToolApprovalCardProps) {
    const { display, editErrors } = props;
    const { tShared } = useSharedTranslation();
    const mode = useDraftMode();
    const fields = display ?? {};
    // Core sends the bare channel name, and none for a direct message.
    const channelName = readString(fields, "channel_name");
    const threadUrl = toSlackUrl(readString(fields, "thread_url"));
    const parentAuthor = readString(fields, "parent_author_name");
    const parentText = readString(fields, "parent_text");
    const mentionNames = isObject(fields.mentions) ? fields.mentions : {};
    const editor = textEditor(props, "text");

    return (
        <DraftSurface>
            <div className="wai-pa-header">
                <ConnectorChannelIcon type="Slack" alt="" className="wai-pa-slack-mark" />
                {/* A channel id means nothing to the reader. Without a channel name, the destination is the linked thread
                    when core sends one. */}
                {channelName !== null
                    ? <Text size="sm" fontWeight="body-sm-semibold" color="neutral" className="wai-pa-channel">#{channelName}</Text>
                    : (
                        <Text size="xs" fontWeight="body-xs-medium" color="neutral-weak">
                            {tShared(threadUrl !== null ? "preparedAction.slackThread" : "preparedAction.slackMessage")}
                        </Text>
                    )}
                {threadUrl !== null && (
                    <Link
                        href={threadUrl}
                        isExternal
                        isQuiet
                        size="xs"
                        variant="secondary"
                        color="neutral-weak"
                        colorHover="neutral"
                        className="wai-pa-thread-link"
                    >
                        {tShared("preparedAction.viewThread")}
                        <IconOpenLink className="wai-pa-thread-link-glyph" aria-hidden="true" />
                    </Link>
                )}
            </div>

            {parentText !== null && <QuotedMessage author={parentAuthor} text={withMentionNames(parentText, mentionNames)} />}

            {mode === "settled"
                ? <SentText>{editor.value}</SentText>
                : <DraftTextArea editor={editor} label={tShared("preparedAction.slackMessage")} isDisabled={mode === "sending"} />}
            <EditErrors errors={editErrors} />
        </DraftSurface>
    );
}

export const sendSlackMessageRenderer: ToolApprovalRenderer = {
    matches: args => readString(args, "text") !== null,
    Card: SlackMessageCard,
    decisionLabels: { allow: "preparedAction.sendToSlack", deny: "preparedAction.discard" }
};
